import React, { useState } from 'react';
import { Plus, Tag, Trash2, X } from 'lucide-react';
import api from '../../lib/api';
import { CARD_LABEL_COLORS } from '../../lib/constants';

const QUALITY_COLORS = { excellent: '#519839', good: '#0079BF', average: '#F2D600', poor: '#DE350B' };

function QualityRating({ task, quality, project, currentUser, onRated }) {
  const [editing, setEditing] = useState(false);
  const isAssignee = (task.assignees || []).some((a) => a.id === currentUser.id);
  const canRate = task.completed && (currentUser.role === 'leader' || (currentUser.role === 'manager' && !isAssignee));

  if (quality && !editing) {
    const color = QUALITY_COLORS[quality.rating] || 'var(--trello-text-muted)';
    return (
      <div style={{ background: 'var(--trello-card-bg)', padding: 8, borderRadius: 4, border: '1px solid var(--trello-border)' }} data-testid="quality-display">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="tm-pill" style={{ background: color + '22', color, fontWeight: 700 }}>{quality.rating}</span>
            <span style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>by {quality.rated_by_name}</span>
          </div>
          {canRate && (
            <button className="tm-btn-ghost" onClick={() => setEditing(true)} style={{ padding: '2px 6px', fontSize: 11 }} data-testid="edit-quality">Edit</button>
          )}
        </div>
        {quality.comment && <div style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>&ldquo;{quality.comment}&rdquo;</div>}
      </div>
    );
  }
  if (!canRate) {
    return (
      <div style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>
        {task.completed ? 'Awaiting manager rating.' : 'Complete the task to enable quality rating.'}
      </div>
    );
  }
  return (
    <div className="flex gap-2 flex-wrap" data-testid="quality-picker">
      {['excellent', 'good', 'average', 'poor'].map((r) => (
        <button key={r} className="tm-btn-ghost" data-testid={`quality-${r}`}
          onClick={async () => {
            const c = prompt(`Optional comment for ${r} rating:`, quality?.comment || '') || '';
            try {
              await api.post(`/rewards/quality/${task.id}`, { rating: r, comment: c });
              setEditing(false);
              onRated?.();
            } catch (e) { alert(e.response?.data?.error || 'Failed'); }
          }}
          style={{ padding: '4px 10px', textTransform: 'capitalize', fontSize: 12 }}>
          {r}
        </button>
      ))}
      {editing && (
        <button className="tm-btn-ghost" onClick={() => setEditing(false)} style={{ padding: '4px 10px', fontSize: 12, color: 'var(--trello-text-muted)' }}>
          Cancel
        </button>
      )}
    </div>
  );
}

function DependenciesEditor({ task, projectTasks, canManage, save }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const depIds = (() => { try { return task.dependencies_json ? JSON.parse(task.dependencies_json) : []; } catch { return []; } })();
  const deps = projectTasks.filter((t) => depIds.includes(t.id));

  return (
    <div>
      <div className="tm-label">Dependencies (blocked by)</div>
      <div style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 4, padding: 6 }}>
        <div className="flex flex-wrap gap-1" data-testid="dep-list">
          {deps.length === 0 && <span style={{ fontSize: 12, color: 'var(--trello-text-muted)', padding: '2px 4px' }}>None. Add tasks that must be completed first.</span>}
          {deps.map((d) => (
            <span key={d.id} className="tm-pill" data-testid={`dep-${d.id}`}
              style={{ background: d.completed ? '#E6F4EA' : '#FFF4E6', color: d.completed ? '#0B6623' : '#7F5A00', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {d.completed ? '✓' : '○'} {d.title.slice(0, 32)}
              {canManage && (
                <button className="tm-btn-ghost" style={{ padding: 0, marginLeft: 2 }}
                  onClick={() => save({ dependencies_json: JSON.stringify(depIds.filter((x) => x !== d.id)) })}
                  data-testid={`dep-del-${d.id}`}><X size={11} /></button>
              )}
            </span>
          ))}
        </div>
        {canManage && (
          <div className="mt-2">
            <button className="tm-btn-ghost" onClick={() => setPickerOpen((v) => !v)} data-testid="dep-add-btn" style={{ padding: '4px 8px', fontSize: 12 }}>
              <Plus size={12} style={{ display: 'inline', verticalAlign: -1, marginRight: 2 }} />Add dependency
            </button>
            {pickerOpen && (
              <div className="mt-2" style={{ background: 'var(--trello-list-bg)', borderRadius: 4, padding: 6 }} data-testid="dep-picker">
                <input className="tm-input" placeholder="Search tasks in this project…" value={search}
                  onChange={(e) => setSearch(e.target.value)} data-testid="dep-search" style={{ marginBottom: 6 }} />
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {projectTasks
                    .filter((t) => !depIds.includes(t.id))
                    .filter((t) => !search || t.title.toLowerCase().includes(search.toLowerCase()))
                    .slice(0, 20)
                    .map((t) => (
                      <button key={t.id} onClick={() => {
                        save({ dependencies_json: JSON.stringify([...depIds, t.id]) });
                        setPickerOpen(false); setSearch('');
                      }} data-testid={`dep-opt-${t.id}`}
                        style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '4px 6px', borderRadius: 3, cursor: 'pointer', fontSize: 13 }}
                        className="hover:bg-white">
                        {t.completed ? '✓ ' : '○ '}{t.title}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Sidebar({
  task, save, canManage, canEditLimited, isAssignee, isLeader,
  project, projectTasks, currentUser, quality, lists, reload,
  mySuggestion, requestOrDelete, deleteReason, setDeleteReason, onSuggestDelete, onClose, onChange,
}) {
  const [showLabels, setShowLabels] = useState(false);
  const canEditTaskFields = isLeader || currentUser.role === 'manager' || isAssignee;

  return (
    <div className="space-y-2">
      {/* <div className="tm-label">Actions</div> */}

      <div>
        <div className="tm-label">Priority</div>
        <select className="tm-input" value={task.priority_v2 || (task.priority === 'now' ? 'critical' : task.priority === 'delegate' ? 'high' : task.priority === 'delete' ? 'low' : 'medium')} onChange={(e) => save({ priority_v2: e.target.value })}
          disabled={!canEditTaskFields} data-testid="detail-priority">
          <option value="critical">🔴 Critical</option>
          <option value="high">🟠 High</option>
          <option value="medium">🟡 Medium</option>
          <option value="low">🔵 Low</option>
        </select>
      </div>

      <div>
        <div className="tm-label">Status</div>
        <select className="tm-input" value={task.task_status || 'new'}
          onChange={(e) => save({ task_status: e.target.value })}
          disabled={!canEditTaskFields} data-testid="detail-status">
          <option value="new">New</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="review">Review</option>
          <option value="testing">Testing</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* <div className="grid grid-cols-4 gap-2"> */}
      <div>
        <div className="tm-label">Start date</div>
        <input className="tm-input" type="date" value={task.start_date ? new Date(task.start_date).toISOString().slice(0, 10) : ''}
          onChange={(e) => save({ start_date: e.target.value || null })}
          disabled={!canEditTaskFields} data-testid="detail-start" />
      </div>
      <div style={{ display: 'none' }}>
        <div className="tm-label">Est. hours</div>
        <input className="tm-input" type="number" step="0.5" min="0" value={task.estimated_hours || ''}
          onChange={(e) => save({ estimated_hours: e.target.value === '' ? null : Number(e.target.value) })}
          disabled={!canManage} data-testid="detail-estimated" />
      </div>

      <div>
        <div className="tm-label">End date</div>
        <input className="tm-input" type="date" value={task.due_date ? new Date(task.due_date).toISOString().slice(0, 10) : ''}
          onChange={(e) => save({ due_date: e.target.value || null })}
          disabled={!canEditTaskFields} data-testid="detail-due" />
      </div>

      {/* </div> */}

      <div>
        <div className="tm-label">Quality rating</div>
        <QualityRating task={task} quality={quality} project={project} currentUser={currentUser} onRated={reload} />
      </div>

      <DependenciesEditor task={task} projectTasks={projectTasks} canManage={canEditTaskFields} save={save} />

      <button className="tm-btn-ghost w-full" style={{ display: 'none', background: task.label_color || '#EBECF0', color: task.label_color ? '#fff' : 'var(--trello-text)', justifyContent: 'flex-start', padding: '8px 10px' }}
        onClick={() => canManage && setShowLabels((v) => !v)} data-testid="label-toggle">
        <Tag size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: -2 }} />Label
      </button>
      {showLabels && (
        <div className="tm-pop">
          <div className="grid grid-cols-4 gap-2">
            {CARD_LABEL_COLORS.map((c) => (
              <button key={c.name} onClick={() => { save({ label_color: c.value }); setShowLabels(false); }}
                style={{ height: 28, borderRadius: 4, background: c.value || 'repeating-linear-gradient(45deg,#EBECF0,#EBECF0 4px,#DFE1E6 4px,#DFE1E6 8px)', border: 'none', cursor: 'pointer' }}
                data-testid={`label-${c.name}`} />
            ))}
          </div>
        </div>
      )}

      {/* <input className="tm-input" type="date" value={task.due_date ? new Date(task.due_date).toISOString().slice(0, 10) : ''}
        onChange={(e) => save({ due_date: e.target.value || null })}
        disabled={!(canManage || canEditLimited)} data-testid="detail-due" /> */}

      <select style={{ display: 'none' }} className="tm-input" value={task.list_id || ''} onChange={(e) => save({ list_id: e.target.value })}
        disabled={!canManage} data-testid="detail-list">
        {(lists || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        {(!lists || !lists.length) && <option value={task.list_id}>{task.list_name || '—'}</option>}
      </select>

      <button className="tm-btn w-full" style={{ background: task.completed ? '#61BD4F' : 'var(--trello-blue)' }}
        onClick={() => save({ completed: !task.completed })}
        disabled={!(canManage || isAssignee)} data-testid="detail-complete-btn">
        {task.completed ? '✓ Completed' : 'Mark complete'}
      </button>

      {(canManage || isLeader) && !task.pending_delete && (
        <>
          {!isLeader && (
            <input className="tm-input" placeholder="Reason (optional)" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} data-testid="delete-reason" />
          )}
          <button className="tm-btn w-full" style={{ background: '#DE350B' }} onClick={requestOrDelete} data-testid="delete-task-btn">
            <Trash2 size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />
            {isLeader ? 'Delete task' : 'Request delete'}
          </button>
        </>
      )}
      {!!task.pending_delete && isLeader && (
        <div className="tm-pop" style={{ background: 'var(--trello-list-bg)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Pending delete</div>
          {task.delete_reason && <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginBottom: 6 }}>Reason: {task.delete_reason}</div>}
          <div className="flex gap-2">
            <button className="tm-btn" style={{ background: '#DE350B', flex: 1 }}
              onClick={async () => { await api.post(`/tasks/${task.id}/delete-decision`, { approve: true }); onChange?.(); onClose(); }}
              data-testid="approve-delete">Approve</button>
            <button className="tm-btn-ghost" style={{ flex: 1 }}
              onClick={async () => { await api.post(`/tasks/${task.id}/delete-decision`, { approve: false }); reload(); onChange?.(); }}
              data-testid="reject-delete">Reject</button>
          </div>
        </div>
      )}

      {/* Suggest Deletion — Team members (or managers not owning the project) */}
      {!canManage && !task.pending_delete && (
        mySuggestion && mySuggestion.status === 'pending' ? (
          <div className="tm-pop" style={{ background: 'var(--trello-list-bg)' }} data-testid="suggestion-pending">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#7F5A00' }}>⏳ Deletion suggestion pending</div>
            <div style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>You proposed deleting this task on {new Date(mySuggestion.created_at).toLocaleDateString()}. Awaiting Leader review.</div>
            <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 4, fontStyle: 'italic' }}>&ldquo;{mySuggestion.reason}&rdquo;</div>
          </div>
        ) : (
          <button className="tm-btn-ghost w-full" style={{ background: 'var(--trello-list-bg)', color: '#7F5A00', justifyContent: 'flex-start', padding: '8px 10px', border: '1px dashed #F2C94C' }}
            onClick={onSuggestDelete} data-testid="suggest-delete-btn">
            <Trash2 size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: -2 }} />
            Suggest deletion
          </button>
        )
      )}
    </div>
  );
}
